import {Box, Button, Container, Text} from '@chakra-ui/react';
import {colors} from 'components/providers';
import OrderCourierMap from 'components/shared/OrderCourierMap';
import useCourierLocation from 'hooks/useCourierLocation';
import {FC} from 'react';
import {TranslationKeys} from 'translations';
import {IOrder, OrderStatusEnum, OrderStatusEnumCourierExpress, ProviderTypeEnum} from 'types';

import {CancelOrder} from '../order-cancel';
import {DeliverTime} from './components/DeliverTime';

interface Props {
  order: IOrder;
  datetime_to_deliver: string;
  providerImg: string;
  short_uuid: string;
  t: (term: TranslationKeys) => string;
  onHelperOnlinePayment: () => void;
  onRetryOnlinePayment: () => void;
  retryTransactionDisabled: boolean;
  isDelivering: boolean;
}

export const OrderStatus: FC<Props> = ({
  short_uuid,
  providerImg,
  t,
  order,
  onHelperOnlinePayment,
  onRetryOnlinePayment,
  retryTransactionDisabled,
  isDelivering,
}) => {
  const {status = {code: OrderStatusEnum.NEW, name: ''}, uuid} = order ?? {};

  const isWaitingPayment = [OrderStatusEnum.WAITING_PAYMENT, OrderStatusEnumCourierExpress.WAITING_PAYMENT].includes(
    status.code,
  );
  const isConfirmedOnlinePayment = [
    OrderStatusEnum.PAYMENT_CONFIRMED,
    OrderStatusEnumCourierExpress.PAYMENT_CONFIRMED,
  ].includes(status.code);
  const isErrorConfirmedOnlinePayment = [
    OrderStatusEnum.PAYMENT_FAILED,
    OrderStatusEnumCourierExpress.PAYMENT_FAILED,
  ].includes(status.code);
  const isProcessing = [OrderStatusEnum.PROCESSING, OrderStatusEnumCourierExpress.PROCESSING].includes(status.code);
  const isOnTheWay = [OrderStatusEnum.DELIVERING_CLIENT, OrderStatusEnumCourierExpress.DELIVERING].includes(
    status.code,
  );
  const isPreparing = [OrderStatusEnum.PREPARING, OrderStatusEnumCourierExpress.PREPARING].includes(status.code);
  const isCanceled = order.is_canceled && !order.delivered;
  const providerType = order.receiver_address
    ? 'courier-express'
    : order.provider.provider_type === ProviderTypeEnum.RESTAURANT
    ? 'restaurant'
    : 'market';

  const isNewOrder = [OrderStatusEnum.NEW, OrderStatusEnumCourierExpress.NEW].includes(status.code);
  const isAutoConfirmed = [OrderStatusEnum.AUTO_CONFIRMED, OrderStatusEnumCourierExpress.AUTO_CONFIRMED].includes(
    status.code,
  );
  const isDelivered_part1 = [OrderStatusEnum.AT_CLIENT_PART1, OrderStatusEnumCourierExpress.AT_CLIENT_PART1].includes(
    status.code,
  );
  const defaultProviderImg = '/food_pattern.webp';
  const isStrausDelivering = order?.provider.straus_delivering;

  const getTextForSpecificProvider = (restaurantTrans: string, marketTrans: string, courierTrans: string) => {
    return providerType === 'market'
      ? marketTrans
      : providerType === 'courier-express'
      ? courierTrans
      : restaurantTrans;
  };

  const geoLocation = useCourierLocation({courierId: order?.courier?.id, shouldRequest: isOnTheWay});

  const canShowCancelOnlineOrder =
    !isCanceled &&
    order?.can_be_canceled &&
    (isWaitingPayment || isErrorConfirmedOnlinePayment) &&
    order?.payment_type_str !== 'cash';

  return (
    <Box overflow="hidden">
      {isOnTheWay && (
        <Box
          h={['350px', '500px']}
          position="relative"
          bg="white"
          borderRadius="12px"
          overflow="hidden"
          zIndex={0}
          mb="20px"
        >
          <OrderCourierMap
            restaurantCoords={{lat: order.branch_data?.latitude ?? 0, lng: order.branch_data?.longitude ?? 0}}
            clientCoords={{lat: order.address.latitude, lng: order.address.longitude}}
            courierCoords={{lat: geoLocation?.latitude ?? 0, lng: geoLocation?.longitude ?? 0}}
            restaurantName={order.provider?.name}
            courierName={order.courier?.first_name}
          />
        </Box>
      )}

      <Container
        variant="white-block"
        h={['auto', geoLocation ? 'auto' : '400px']}
        position="relative"
        display="flex"
        bgImage={providerImg ?? defaultProviderImg}
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
      >
        <Container
          variant="white-block"
          mx="auto"
          textAlign="center"
          alignSelf="flex-end"
          border="1px solid"
          borderColor={providerImg ? 'transparent' : colors.lightGray}
          position="relative"
        >
          <Text>
            {t('ORDER_NUMBER')} {short_uuid}
          </Text>
          {isCanceled && (
            <CancelOrder
              is_can_be_canceled={order?.can_be_canceled}
              isCanceled={isCanceled}
              t={t}
              uuid={uuid}
              order={order}
            />
          )}
          {isAutoConfirmed && isNewOrder && <Text>{t('ORDER_STATUS_AUTO_CONFIRMED')}</Text>}
          {!isPreparing && !isDelivering && !isWaitingPayment && !isConfirmedOnlinePayment && (
            <>
              {!isCanceled && order?.can_be_canceled && (
                <>
                  {!isProcessing || isStrausDelivering ? (
                    <Text fontWeight="600" mb="15px" color={colors.textGray}>
                      {getTextForSpecificProvider(
                        t('ORDER_STATUS_1_NEW_ORDER_FOR_RESTAURANT'),
                        t('ORDER_STATUS_1_NEW_ORDER_FOR_MARKET'),
                        t('orderDetailsCourierExpressStatus1'),
                      )}
                    </Text>
                  ) : (
                    <Text fontWeight="600" mb="15px" color={colors.textGray}>
                      {t('RESTAURANT_DELIVERING')}
                    </Text>
                  )}
                </>
              )}
              {!isCanceled && order?.can_be_canceled && order?.payment_type_str === 'cash' && (
                <CancelOrder
                  is_can_be_canceled={order?.can_be_canceled}
                  isCanceled={isCanceled}
                  t={t}
                  uuid={uuid}
                  order={order}
                />
              )}
            </>
          )}

          {/* order can have status c30 and is_canceled true, to avoid unused */}
          {!isCanceled ? (
            <>
              {isErrorConfirmedOnlinePayment ? (
                <>
                  <Text color={colors.red}>{status?.name}</Text>
                  <Button
                    disabled={retryTransactionDisabled}
                    onClick={onRetryOnlinePayment}
                    bg={colors.green}
                    color={colors.white}
                    mt="5px"
                  >
                    {t('PAY_ORDER')}
                  </Button>
                </>
              ) : (
                <>{isErrorConfirmedOnlinePayment && <Text>{t('ERROR_ORDER_IS_PAID')}</Text>}</>
              )}
              {isConfirmedOnlinePayment && order?.is_payment_successful && <Text>{t('ORDER_IS_PAID')}</Text>}
              {isWaitingPayment && (
                <>
                  <Text>{status?.name}</Text>
                  <Button onClick={onHelperOnlinePayment} bg={colors.green} color={colors.white} mt="5px">
                    {t('PAY_ORDER')}
                  </Button>
                </>
              )}
              {canShowCancelOnlineOrder && (
                <Box flex="1" mt="10px" flexDirection="column">
                  <CancelOrder
                    is_can_be_canceled={order?.can_be_canceled}
                    isCanceled={isCanceled}
                    t={t}
                    uuid={uuid}
                    order={order}
                  />
                </Box>
              )}
              {isDelivering && !isDelivered_part1 && !isOnTheWay && (
                <Text fontWeight="600" fontSize={['12px', '18px']} color={colors.textGray}>
                  {getTextForSpecificProvider(
                    t('ORDER_STATUS_3_RESTAURANT'),
                    t('ORDER_STATUS_3_MARKET'),
                    t('orderDetailsCourierExpressStatus3'),
                  )}
                </Text>
              )}
              {!isDelivered_part1 && isOnTheWay && (
                <Text fontWeight="600" fontSize={['16px', '18px']} color={colors.textGray}>
                  {t('COURIER_RIDES')}
                </Text>
              )}
              {isPreparing && (
                <Text fontSize="18px" color={colors.textGray} fontWeight="600">
                  {getTextForSpecificProvider(
                    `${isStrausDelivering ? t('ORDER_STATUS_2_FOR_RESTAURANT') : t('RESTAURANT_DELIVERING')}`,
                    t('ORDER_STATUS_2_FOR_MARKET'),
                    t('orderDetailsCourierExpressStatus2'),
                  )}
                </Text>
              )}
              {isDelivered_part1 && (
                <Text fontSize="18px" color={colors.textGray} fontWeight="600">
                  {t('ORDER_STATUS_4')}
                </Text>
              )}
              {(isPreparing || isDelivering || isDelivered_part1) && (
                <DeliverTime
                  isPreorder={!!order.preorder}
                  deliverTime={order.preorder ? order.preorder : order.datetime_to_deliver}
                />
              )}
            </>
          ) : null}
        </Container>
        {/*{(isCourierAtClient || isDelivered) && (*/}
        {/*  <Duration t={t} time={delivered_time} />*/}
        {/*)}*/}
      </Container>
    </Box>
  );
};
